import { Typography, Box, CircularProgress, Button, Stack } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import MapOptionsMenu from './MapOptionsMenu';
import MapFilterMenu from './MapFilterMenu';
import FloatingPagination from './FloatingPagination';
import EventMap from './EventMap';
import { useCallback, useContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import MapIconDrawer from './MapIconDrawer';
import TodoIconDrawer from './TodoIconDrawer';
import { MapContext, ViewOnlyContext, useEventDetails } from '../context';
import { StationType, TodoType } from '../schema';
import NoEventError from './NoEventError';
import L from 'leaflet';
import { useLocation } from 'react-router-dom';

interface Props {
    eventId: string;
}

export default function EventMapContainer(props: Props) {
    const viewOnly = useContext(ViewOnlyContext);
    const location = useLocation();
    const [editMode, setEditMode] = useState(false);
    // Initialize todoMode from location state (only on mount)
    const [todoMode, setTodoMode] = useState(() => !viewOnly && !!location.state?.enableTodoMode);

    const { eventId } = props;
    const { event, isLoading, error } = useEventDetails(eventId);

    const [mapNumber, setMapNumber] = useState(() => {
        if (location.state?.sweepArea) {
            return location.state.sweepArea.mapNumber ?? 0;
        }
        return 0;
    });
    const [cleanupMode, setCleanupMode] = useState(false);
    const [currentStation, setCurrentStation] = useState<StationType | null>(null);
    const [currentTodoType, setCurrentTodoType] = useState<TodoType | null>(null);
    const [sweepRect, setSweepRect] = useState<L.LatLngBounds | null>(null);
    const [selectedStationTypeIds, setSelectedStationTypeIds] = useState<Set<string>>(new Set());
    const [todoOnlyFilter, setTodoOnlyFilter] = useState(false);
    const [notesOnlyFilter, setNotesOnlyFilter] = useState(false);
    const [loadingTimeout, setLoadingTimeout] = useState(false);
    const [mapReady, setMapReady] = useState(false);

    const hasHandledLocationNavRef = useRef(false);
    const locationStateKeyRef = useRef<string | null>(null);

    useLayoutEffect(() => {
        if (!event) return;

        const currentStateKey =
            location.state?.mapId || location.state?.sweepArea
                ? JSON.stringify({ mapId: location.state?.mapId, sweepArea: location.state?.sweepArea })
                : null;

        if (currentStateKey !== locationStateKeyRef.current) {
            hasHandledLocationNavRef.current = false;
            locationStateKeyRef.current = currentStateKey;
        }

        if (!hasHandledLocationNavRef.current && location.state?.mapId) {
            const mapIndex = event.maps?.findIndex(m => m.id === location.state.mapId) ?? -1;
            if (mapIndex !== -1 && mapIndex !== mapNumber) {
                // eslint-disable-next-line
                setMapNumber(mapIndex);
                // reset state when changing maps
                setMapReady(false);
                hasHandledLocationNavRef.current = true;
                return;
            }
            hasHandledLocationNavRef.current = true;
        }

        setMapReady(true);
    }, [event, location.state?.mapId, location.state?.sweepArea, mapNumber]);

    useEffect(() => {
        if (!isLoading) return;

        const timer = setTimeout(() => {
            setLoadingTimeout(true);
        }, 5000);

        return () => {
            clearTimeout(timer);
            setLoadingTimeout(false);
        };
    }, [isLoading]);

    const toggleEditMode = useCallback(() => {
        // only allow edit mode to be enabled
        // when view-only mode is not active.
        setEditMode(!viewOnly && !editMode);
        setCurrentStation(null);
    }, [viewOnly, editMode, setEditMode, setCurrentStation]);

    const toggleTodoMode = useCallback(() => {
        // only allow edit mode to be enabled
        // when view-only mode is not active.
        setTodoMode(!viewOnly && !todoMode);
        setCurrentStation(null);
        setCurrentTodoType(null);
        setSweepRect(null);
    }, [viewOnly, todoMode, setTodoMode, setCurrentStation]);

    const toggleCleanupMode = useCallback(() => {
        setCleanupMode(!cleanupMode);
    }, [cleanupMode, setCleanupMode]);

    if (isLoading) {
        if (loadingTimeout) {
            return (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" padding={4} height="100%">
                    <Stack spacing={3} alignItems="center" maxWidth={400} textAlign="center">
                        <WifiOffIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                        <Typography variant="h6" color="text.primary">
                            Unable to Load Event
                        </Typography>
                        <Typography color="text.secondary">
                            This event isn&apos;t cached for offline use and we can&apos;t reach the server. Please check your internet
                            connection and try again.
                        </Typography>
                        <Button variant="contained" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </Stack>
                </Box>
            );
        }
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" padding={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">Error loading event details: {error.message}</Typography>;
    }

    if (!event) {
        return <NoEventError eventId={eventId} />;
    }

    const maps = event?.maps || [];

    // Automatically open bottom drawer in edit mode
    const stationDrawerOpen = editMode;
    const todoDrawerOpen = todoMode;

    const mapContext = {
        editMode,
        todoMode,
        toggleEditMode,
        toggleTodoMode,
        cleanupMode,
        toggleCleanupMode,
        currentStation,
        setCurrentStation,
        currentTodoType,
        setCurrentTodoType,
        eventId,
        mapNumber,
        sweepRect,
        setSweepRect,
        selectedStationTypeIds,
        setSelectedStationTypeIds,
        todoOnlyFilter,
        setTodoOnlyFilter,
        notesOnlyFilter,
        setNotesOnlyFilter,
    };
    console.log('Current Station ', currentStation);

    return (
        <MapContext.Provider value={mapContext}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 0,
                }}
            >
                <Box sx={{ flex: 1, position: 'relative' }}>
                    {mapReady && <EventMap mapNumber={mapNumber} />}
                    <MapFilterMenu />
                    <MapOptionsMenu />
                    {maps.length > 1 && (
                        <FloatingPagination
                            numPages={maps.length}
                            currentPage={mapNumber}
                            setPage={setMapNumber}
                            mapNames={maps.map(m => m?.name as string)}
                        />
                    )}
                </Box>

                <MapIconDrawer
                    open={stationDrawerOpen}
                    todo={false}
                    currentStation={currentStation}
                    setCurrentStation={setCurrentStation}
                />
                <TodoIconDrawer open={todoDrawerOpen} currentTodoType={currentTodoType} setCurrentTodoType={setCurrentTodoType} />
            </Box>
        </MapContext.Provider>
    );
}
